import React, { useState } from 'react';
import { useAuth } from '../../services/AuthContext';
import { BaseControlProps } from "../../models/BaseControlProps";
import { AuthUserResetPasswordRequest } from "../../models/AuthUserResetPasswordRequest";
import { UtilService } from '../../services/UtilService';
import { UserAPIService } from '../../services/UserAPIService';
import { Box, Button, CircularProgress, Divider, FormControl, FormHelperText, FormLabel, TextField, Typography } from '@mui/material';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';

const initialState = {
    userName: "",
    newPassword: "",
    confirmPassword: "",
    oldPassword: "",
    encodedGuid: "",
    encodedUserId: ""
}

const Security = (props: BaseControlProps) => {
    const [scope, apply] = React.useState<AuthUserResetPasswordRequest>(initialState)
    const authService = new UserAPIService();
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const [oldPasswordError, setOldPasswordError] = useState(false);
    const [oldPasswordErrorMessage, setOldPasswordErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');
    const [inputValid, isInputValid] = useState(false);
    const [isLoading, setIsLoading]  = useState(false); 

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    const resetPassword = async(event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!inputValid) { return; }
        setIsLoading(true);
        scope.userName = authContext.emailAddress;
        authService.resetPassword(scope, authContext.token).then((response)=>{
            if(response.error) {
                showMessage(response.error, 'error');
            }
            else{
                if(response.response?.reset){
                    apply(initialState);
                    showMessage("Password reseted successfully!", "success");
                }
                else{
                    showMessage("We are sorry, but we couldn’t register you, please contact Mercer", 'error');
                }
            }

            setIsLoading(false);
            scope.userName = '';
            scope.newPassword = '';
            scope.oldPassword = '';
            scope.confirmPassword = '';
            applyScope();
        })
    }

    const validateInputs = () => {
        const oldPassword = document.getElementById('oldPassword') as HTMLInputElement;
        const password = document.getElementById('newPassword') as HTMLInputElement;
        const confirmPassword = document.getElementById('confirmPassword') as HTMLInputElement;
        const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_#])[A-Za-z\d@$!%*?&_#]{8,}$/;
        scope.newPassword = password.value;
        scope.oldPassword = oldPassword.value;
        applyScope();
        let isValid = true;

        if (!oldPassword.value) {
          setOldPasswordError(true);
          setOldPasswordErrorMessage('Password must be at least 8 characters long.');
          isValid = false;
        } else {
          setOldPasswordError(false);
          setOldPasswordErrorMessage('');
        }

        if (!password.value || password.value.length < 8) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must be at least 8 characters long.');
          isValid = false;
        } else if (!passwordPattern.test(password.value)) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.');
          isValid = false;
        } else {
          setPasswordError(false);
          setPasswordErrorMessage('');
        }
    
        if (!confirmPassword.value || confirmPassword.value !== password.value) {
          setConfirmPasswordError(true);
          setConfirmPasswordErrorMessage('Password and Confrim Password does not match.');
          isValid = false;
        } else {
          setConfirmPasswordError(false);
          setConfirmPasswordErrorMessage('');
        }

        if (!isValid) {
            isInputValid(false);
        } else {
            isInputValid(true);
        }
    
        return isValid;
      };

    return(
        <>
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Change Password
                    </Typography>
                </Box>
                <Divider />
            </Box>
            <Box
                component='form'
                onSubmit={resetPassword}
                sx={{ 
                    maxWidth: 600, // Maximum width of the form
                    margin: '20 auto', // Center the form horizontally
                    padding: 2, // Padding around the form
                    border: '1px solid #ddd', // Optional border for better visibility
                    borderRadius: 1 // Optional rounded corners
                }}
            >
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormLabel id="old-password-label" htmlFor='old-password'>Old Password : </FormLabel>
                    <TextField 
                        required
                        fullWidth
                        id="oldPassword"
                        name="oldPassword"
                        placeholder="••••••••"
                        type="password"
                        variant="outlined"
                        onChange={(e) => {
                            scope.oldPassword = e.target.value;
                            applyScope();
                        }}
                        value={scope.oldPassword}
                        inputProps={{
                            'aria-label': '••••••••',
                        }}
                        error={oldPasswordError}
                        helperText={oldPasswordErrorMessage}
                        color={oldPasswordError ? 'error' : 'primary' }
                    />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormLabel id="passwordLabel" htmlFor="password">Password : </FormLabel>
                    <TextField 
                        required
                        fullWidth
                        id="newPassword"
                        name="newPassword"
                        placeholder="••••••••"
                        type='password'
                        variant='outlined'
                        error={passwordError}
                        onChange={(e)=>{
                            scope.newPassword = e.currentTarget.value;
                            applyScope();
                        }}
                        value={scope.newPassword}
                        inputProps={{
                            'aria-label': '••••••••',
                        }}
                        helperText={passwordErrorMessage}
                        color={passwordError ? 'error' : 'primary' }
                    />
                    <FormHelperText sx={{ mt: 1, fontSize: '0.875rem' }}> 
                        <i>(Password must be minimum 8 characters with at least 1 uppercase letter, 1 lower case letter, 1 number, 1 special character)</i>
                    </FormHelperText>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormLabel htmlFor='confirmPassword'>Confirm your password : </FormLabel>
                    <TextField
                        required
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="••••••••"
                        type='password' 
                        variant='outlined'
                        onChange={(e)=>{
                            scope.confirmPassword = e.currentTarget.value;
                            applyScope();
                        }}
                        value={scope.confirmPassword}
                        error={confirmPasswordError}
                        helperText={confirmPasswordErrorMessage}
                        color={confirmPasswordError ? 'error' : 'primary' }
                    />
                </FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Other content */}
                    </Box>
                    <Box sx={{ marginLeft: 'auto' }}>
                        <Button
                            type="submit"
                            fullWidth 
                            variant="contained" 
                            disabled={isLoading} 
                            onClick={validateInputs}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{
                                '&.Mui-disabled': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                }
                            }}
                        >
                            {isLoading ? 'Loading...' : 'Reset Password'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default Security;